import { Controller } from "stimulus";
import { delegate } from 'utils/delegate';
import { injectDom } from "utils/injectDom";
import { logEvent } from "utils/analytics";
import confetti from "canvas-confetti";
export default class extends Controller {
  static targets = ["content", "loading", "modal"];

  connect() {
    if (this.element.dataset.showOnLoad === "true") {
      this.changeVisibility('loaded');
    } else {
      delegate({
        selector: '.modal-link',
        eventName: 'click',
        handler: this.loadModalFromEvent.bind(this)
      });

      window.renderModal = this.renderModal.bind(this);
    }
  }

  disconnect() {
    this.cleanup();
    this.changeVisibility('closed');
  }

  loadModalFromEvent(e) {
    e.preventDefault();

    const link = e.target.closest('a');
    const url = new URL(link.href, window.location.origin);
    url.searchParams.set("modal", "true");

    // On hotwire native, we don't want to override the navigation, just add a parameter to the URL to instruct the web view to show a modal.
    if (document.querySelector("html").dataset.hotwireNative) {
      window.location.href = url.toString();
      return;
    }

    this.renderModal(url.toString(), { noPad: link.dataset.modalStyle === "no-pad", linkText: link.textContent });
  }

  async renderModal(url, { noPad = false, linkText = null } = {}) {
    this.changeVisibility('loading');

    this.currentModalUrl = url;
    logEvent("Modal - Loading", { url, linkText });

    if (noPad) {
        this.modalTarget.classList.remove("p-4");
        this.modalTarget.classList.remove("lg:pl-8");
    } else {
        this.modalTarget.classList.add("p-4");
        this.modalTarget.classList.add("lg:pl-8");
    }

    const newDom = await this.fetchModal();
    await injectDom(this.contentTarget, newDom);
    
    if (window.supercast) {
      window.supercast.trigger('pageload');
      window.supercast.trigger('modal-loaded');
    }
    logEvent("Modal - Loaded", { url });
    this.changeVisibility('loaded');

    // Reset hasInteracted since this is a new modal.
    this.hasInteracted = false;
  }

  interacted() {
    if (this.hasInteracted === false) {
      logEvent("Modal - Interaction", { url: this.currentModalUrl });
      this.hasInteracted = true;
    }
  }

  async fetchModal() {
    const url = this.currentModalUrl;
    const response = await fetch(url, { headers: { 'Accept': 'text/html' } });
    const html = await response.text();
    const dom = document.createElement('div');
    dom.innerHTML = html;
    const replacement = dom.querySelector(".modal-content");
    if (!replacement) {
      throw new Error("Could not find modal content in response");
    }
    return replacement;
  }
  
  close(e) {
    e.preventDefault();
    logEvent("Modal - Closed", { url: this.currentModalUrl });

    this.cleanup();
    this.changeVisibility('closed');
  }

  cleanup() {
    // This should keep the turbo frame intact while still getting rid of all 
    // modal content.
    this.contentTarget.innerHTML = "";
    if (this.element.dataset.showOnLoad === "true") {
      this.element.remove();
    }
  }

  changeVisibility(state) {
    if (state === 'loaded') {
      this.loadingTarget.hidden = true;
      this.modalTarget.hidden = false;
      this.element.hidden = false;
      this.modalTarget.classList.remove(...['translate-y-full', 'md:opacity-0', 'md:scale-50']);
      this.modalTarget.classList.add(...['translate-y-0', 'md:opacity-100', 'md:scale-100']);
      this.maybeShowConfetti();
    } else if (state === 'loading') {
      this.loadingTarget.hidden = false;
      this.modalTarget.hidden = false;
      this.element.hidden = false;
    } else if (state === 'closed') {
      this.loadingTarget.hidden = true;
      this.modalTarget.hidden = true;
      this.element.hidden = true;
      this.modalTarget.classList.remove(...['translate-y-[95%]', 'translate-y-0', 'md:opacity-0', 'md:scale-50']);
      this.modalTarget.classList.add(...['translate-y-full', 'md:opacity-100', 'md:scale-100']);
    }
  }

  maybeShowConfetti() {
    if (this.element.dataset.confetti === "true") {
      confetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0 }
      });
      confetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1 }
      });
    }
  }
}
